import { BriefPage } from 'features/briefs/components/brief-page'
import type { Contact } from 'types/list'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CONFIRM_AND_SEND_TAB } from 'features/briefs/utils/nav'
import { useDispatch, useSelector } from 'hooks/use-redux'
import {
  getBrief,
  getBriefContacts,
  removeRecipient,
  BriefContact,
  toggleAllRecipients,
  toggleRecipient,
  saveRecipients,
} from '@disco/state-management/reducers/entities/briefs'
import { Box, Callout, Checkbox, Flex, Text } from 'components/ds'
import DataTable, { Column } from 'components/ui/data-table'
import UserAvatar from 'components/ui/user-avatar'
import Button from 'components/ui/button'
import Heading from 'components/ui/heading'
import { MdInfoOutline } from '@react-icons/all-files/md/MdInfoOutline'
import Spinner from 'components/ui/spinner'
import { formatDateWithTimezone, formatDate } from '@disco/utils/time-utils'
import { LockIcon } from 'components/ui/icon/svg-icons'
import { isContactsLoaded } from '@disco/state-management/selectors/contacts-all'
import styles from './index.module.css'
import { BriefRecipient } from 'types/brief'
import { RemoveRecipientConfirmation } from 'features/briefs/components/remove-recipient-confirmation'

export const BriefEditorConfirmPage = () => {
  const params = useParams<{ briefId: string }>()
  const brief = useSelector(state =>
    getBrief(state, { briefId: parseInt(params.briefId!) }),
  )

  const contactsLoaded = useSelector(isContactsLoaded)

  const dispatch = useDispatch()

  const briefContacts = useSelector(state =>
    getBriefContacts(state, { briefId: parseInt(params.briefId!) }),
  )

  const [recipientToRemove, setRecipientToRemove] =
    useState<BriefRecipient | null>(null)

  const onRemoveRecipient = async (
    recipient: BriefRecipient,
    confirmed: boolean = false,
  ) => {
    if (!brief) return

    if (recipient?.sent_at && !confirmed) {
      setRecipientToRemove(recipient)
      return
    }

    if ('id' in recipient) {
      await dispatch(
        removeRecipient({
          id: parseInt(params.briefId!),
          recipientId: recipient.id,
          forceSave: confirmed,
        }),
      )
    } else {
      await dispatch(
        removeRecipient({
          id: parseInt(params.briefId!),
          email: recipient.email,
          forceSave: confirmed,
        }),
      )
    }
    if (confirmed) {
      await dispatch(saveRecipients(brief.id))
    }
  }

  const columns: Column[] = useMemo(
    () => [
      {
        width: 'content',
        headerRender: () => {
          const allSelected =
            brief?.recipients_list?.length > 0 &&
            brief?.recipients_list?.every(recipient => recipient.selected)
          return (
            <Checkbox
              checked={allSelected}
              onCheckedChange={() => {
                dispatch(
                  toggleAllRecipients({
                    id: parseInt(params.briefId!),
                    selected: !allSelected,
                  }),
                )
              }}
            />
          )
        },
        render: (prop: BriefContact) => {
          return (
            <Checkbox
              checked={prop.selected}
              onCheckedChange={() => {
                dispatch(
                  toggleRecipient({
                    id: parseInt(params.briefId!),
                    recipientId: 'id' in prop ? prop.id : undefined,
                    email:
                      'type' in prop && prop.type === 'email'
                        ? prop.email
                        : undefined,
                    selected: !prop.selected,
                  }),
                )
              }}
            />
          )
        },
      },
      {
        contentType: 'button',
        width: 'content',
        name: 'NAME',
        render: (prop: BriefContact) => (
          <UserAvatar
            name={'name' in prop && prop.name ? prop.name : undefined}
            size="24px"
            fontSize="11px"
          />
        ),
      },
      {
        id: 'name',
      },
      {
        id: 'email',
        name: 'EMAIL',
      },
      {
        id: 'sent_at',
        name: 'LAST SENT',
        width: 'small',
        render: (sent_at: string | null) =>
          sent_at ? formatDate(sent_at) : null,
      },
      {
        contentType: 'button',
        width: 'content',
        name: '',
        render: (prop: BriefRecipient) => (
          <Button
            appearance="icon"
            caption="×"
            onClick={() => onRemoveRecipient(prop)}
          />
        ),
      },
    ],
    [onRemoveRecipient],
  )

  if (!brief) return <Spinner />

  const formattedDeadline = brief.deadline
    ? formatDateWithTimezone(brief.deadline, brief?.deadline_timezone)
    : null

  const selectedCount = brief?.recipients_list?.filter(
    recipient => recipient.selected,
  ).length

  return (
    <BriefPage step={CONFIRM_AND_SEND_TAB} title={CONFIRM_AND_SEND_TAB}>
      {/* Recipients Section */}
      <Flex justify="between">
        <Box>
          <Heading size={500}>Recipients</Heading>
          {contactsLoaded ? (
            <Text size="2">Sending to {selectedCount} of your contacts</Text>
          ) : null}
        </Box>
        <Flex align="center" gap="1">
          <LockIcon size={12} />
          <Text size="1">Only visible to sender</Text>
        </Flex>
      </Flex>

      <Box mt="4">
        {contactsLoaded ? (
          <DataTable
            appearance="plain"
            columns={columns}
            data={briefContacts}
            emptyState={<Text size="2">No recipients selected.</Text>}
          />
        ) : (
          <Spinner />
        )}

        <Callout.Root color="blue" mt="3">
          <Callout.Icon>
            <MdInfoOutline />
          </Callout.Icon>
          <Callout.Text>
            Recipients will not see each other&#39;s names or email addresses.
          </Callout.Text>
        </Callout.Root>
      </Box>

      {/* Brief Details Section */}
      <Box mt="6">
        <Heading size={500}>Brief Details</Heading>

        <Box mt="4">
          <Box>
            <Text size="2" as="div" color="gray">
              Brief Title
            </Text>
            <Text size="2" as="div">
              {Boolean(brief.name)
                ? brief.name
                : 'Enter a title on the Brief Content tab.'}
            </Text>
          </Box>

          <Box mt="4">
            <Text size="2" as="div" color="gray">
              Brief Deadline
            </Text>
            <Text size="2" as="div">
              {formattedDeadline ||
                'Enter a deadline on the Brief Content tab.'}
            </Text>
          </Box>

          <Box mt="5" maxWidth="600px">
            <Text size="2" as="div" color="gray">
              Description
            </Text>
            <Text
              size="2"
              as="div"
              className={styles.description}
              dangerouslySetInnerHTML={{
                __html: Boolean(brief.description_html)
                  ? brief.description_html
                  : 'Enter a description on the Brief Content tab.',
              }}
            />
          </Box>
        </Box>
      </Box>
      {recipientToRemove && (
        <RemoveRecipientConfirmation
          onClose={removeRecipient => {
            if (removeRecipient) {
              onRemoveRecipient(recipientToRemove, true)
            }
            setRecipientToRemove(null)
          }}
        />
      )}
    </BriefPage>
  )
}
